import type { ConversationMessageRow } from '@axilon-apps/domains-ontology-ui-state'
import { useDebugMode } from '../../lib/debug-mode'
import CollapsibleToolCall from './CollapsibleToolCall'

/**
 * Flat rendering of a persisted LLM message stream.
 */

interface ToolUseBlock {
  type: 'tool_use'
  id: string
  name: string
  input: unknown
}

interface TextBlock {
  type: 'text'
  text: string
}

interface NormalizedToolResult {
  toolUseId: string
  content: unknown
  isError: boolean
}

/** One {tool_use, tool_result?} pair flattened into a row-ready shape. */
export interface FlatToolCall {
  id: string
  name: string
  input: unknown
  output: unknown
  status: 'running' | 'completed' | 'error'
  /** Shared by every `tool_use` that landed in the same assistant message. */
  batchId: string
}

function isToolUseBlock(b: unknown): b is ToolUseBlock {
  if (typeof b !== 'object' || b === null) return false
  const candidate = b as ToolUseBlock
  return (
    candidate.type === 'tool_use' &&
    typeof candidate.id === 'string' &&
    typeof candidate.name === 'string'
  )
}

function isTextBlock(b: unknown): b is TextBlock {
  if (typeof b !== 'object' || b === null) return false
  const candidate = b as TextBlock
  return candidate.type === 'text' && typeof candidate.text === 'string'
}

/**
 * Extract a normalized tool_result from a content block. Accepts either:
 *
 *   - Standard Anthropic shape: `{type, tool_use_id, content, is_error?}`
 *   - Proxy-server custom shape: `{type, toolResult: {toolUseId, content}}`
 *     (emitted by `ToolExecutionMessageBuilder` — see
 *     `agentic-core/services/tool-execution-message-builder.ts`).
 *
 * Returns `null` for non-tool-result blocks.
 */
function asToolResult(block: unknown): NormalizedToolResult | null {
  if (typeof block !== 'object' || block === null) return null
  const b = block as Record<string, unknown>
  if (b.type !== 'tool_result') return null

  if (typeof b.tool_use_id === 'string') {
    return {
      toolUseId: b.tool_use_id,
      content: b.content ?? null,
      isError: b.is_error === true
    }
  }

  if (typeof b.toolResult === 'object' && b.toolResult !== null) {
    const tr = b.toolResult as Record<string, unknown>
    if (typeof tr.toolUseId === 'string') {
      return {
        toolUseId: tr.toolUseId,
        content: tr.content ?? null,
        isError: tr.isError === true || tr.is_error === true
      }
    }
  }

  return null
}

/** Only `role` + `content` are read, so any persisted-message-shaped row works
 * (e.g. the agent-ingest transcript's lighter `{ role, content }`). */
type MessageLike = Pick<ConversationMessageRow, 'role' | 'content'>

function blocksFromMessage(message: MessageLike): unknown[] {
  const content = message.content
  if (Array.isArray(content)) return content
  return []
}

/**
 * Flatten a persisted LLM message stream into an ordered list of tool
 * calls. tool_use blocks define the order; tool_result blocks (which may
 * arrive in a later message) are matched back via tool_use_id.
 *
 * Unmatched tool_uses (e.g. a stream that ended mid-flight) keep
 * `status: 'running'` and `output: null` so the UI can still render them.
 */
export function flattenMessagesToToolCalls(messages: MessageLike[]): FlatToolCall[] {
  // Build the tool_use_id → tool_result lookup once across the whole stream.
  const resultsById = new Map<string, NormalizedToolResult>()
  for (const message of messages) {
    if (message.role !== 'user') continue
    for (const block of blocksFromMessage(message)) {
      const normalized = asToolResult(block)
      if (normalized) resultsById.set(normalized.toolUseId, normalized)
    }
  }

  const out: FlatToolCall[] = []
  let assistantTurn = 0
  for (const message of messages) {
    if (message.role !== 'assistant') continue
    const toolBlocks = blocksFromMessage(message).filter(isToolUseBlock)
    if (toolBlocks.length === 0) continue
    const batchId = `turn-${assistantTurn}`
    assistantTurn += 1
    for (const block of toolBlocks) {
      const result = resultsById.get(block.id)
      out.push({
        id: block.id,
        name: block.name,
        input: block.input,
        output: result?.content ?? null,
        status: result === undefined ? 'running' : result.isError ? 'error' : 'completed',
        batchId
      })
    }
  }
  return out
}

/** An assistant turn's leading text paired with the tool_use that committed it. */
export interface AssistantTurnText {
  /** Concatenated, trimmed text blocks that preceded the turn's first tool_use. */
  text: string
  /** Name of that tool_use — the commit sentinel. */
  toolName: string
  /** Raw input of that tool_use (carries step_index for dispatch_subagent). */
  toolInput: unknown
}

/**
 * Reconstruct committed chain-of-thought segments from persisted messages.
 * Mirrors the live progress hook's boundary: text accumulated before a
 * tool_use is one committed segment. Returns one entry per assistant turn
 * that emitted leading text and ended in a tool_use; turns whose leading
 * text is empty/whitespace are skipped, matching the live hook ignoring
 * empty deltas.
 */
export function extractAssistantTurnTexts(messages: MessageLike[]): AssistantTurnText[] {
  const out: AssistantTurnText[] = []
  for (const message of messages) {
    if (message.role !== 'assistant') continue
    let text = ''
    for (const block of blocksFromMessage(message)) {
      if (isTextBlock(block)) {
        text += block.text
        continue
      }
      if (isToolUseBlock(block)) {
        const trimmed = text.trim()
        if (trimmed.length > 0) {
          out.push({ text: trimmed, toolName: block.name, toolInput: block.input })
        }
        break
      }
    }
  }
  return out
}

interface ToolCallStreamProps {
  /** Pre-flattened calls. Pass `flattenMessagesToToolCalls(messages)` if you have raw rows. */
  calls: FlatToolCall[]
  /** Empty-state copy when `calls.length === 0`. */
  emptyText?: string
}

/**
 * Render a flat sequence of tool calls. One `CollapsibleToolCall` per row,
 * numbered 1..N in stream order. Batch grouping lives on the chat debug
 * tool log (`SidebarToolCallList`), which has the live overlap signal.
 */
const ToolCallStream = ({ calls, emptyText }: ToolCallStreamProps) => {
  const debugMode = useDebugMode()

  if (!debugMode) return null

  if (calls.length === 0) {
    return <p className="m-0 text-xs text-ink-3">{emptyText ?? 'No tool calls.'}</p>
  }
  return (
    <div className="flex flex-col gap-1">
      {calls.map((tc, i) => (
        <CollapsibleToolCall
          key={tc.id}
          index={i}
          name={tc.name}
          input={tc.input}
          output={tc.output}
          status={tc.status}
        />
      ))}
    </div>
  )
}

export default ToolCallStream
